import React, { useState, useEffect, useRef } from 'react';
import Icon from '../AppIcon';
import Button from '../ui/Button';

const ProjectActionToolbar = ({ 
  projectId,
  projectName,
  onEdit,
  onChat,
  onGenerateReport,
  onExport,
  onShare,
  className = ''
}) => {
  const [isSticky, setIsSticky] = useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsSticky(scrollPosition > 200);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const actions = [
    {
      id: 'edit',
      label: 'Editar Proyecto',
      icon: 'Edit',
      variant: 'outline',
      onClick: onEdit,
      tooltip: 'Modificar información del proyecto'
    },
    {
      id: 'chat',
      label: 'Iniciar Chat',
      icon: 'MessageSquare',
      variant: 'outline',
      onClick: onChat,
      tooltip: 'Comunicarse con agentes especializados'
    },
    {
      id: 'report',
      label: 'Generar Informe',
      icon: 'FileText',
      variant: 'default',
      onClick: onGenerateReport,
      tooltip: 'Crear reporte técnico del proyecto'
    }
  ];

  const moreActions = [
    {
      id: 'export',
      label: 'Exportar Datos',
      icon: 'Download',
      onClick: onExport
    },
    {
      id: 'share',
      label: 'Compartir',
      icon: 'Share2',
      onClick: onShare
    }
  ];

  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (moreMenuRef?.current && !moreMenuRef?.current?.contains(event?.target)) {
        setShowMoreMenu(false);
      }
    };

    if (showMoreMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMoreMenu]);

  return (
    <div
      className={`
        ${isSticky ? 'sticky top-16 z-50 shadow-md' : ''}
        bg-card border-b border-border transition-all duration-250
        ${className}
      `}
    >
      <div className="px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg flex-shrink-0">
              <Icon name="FolderTree" size={20} color="var(--color-primary)" />
            </div>
            <div className="min-w-0">
              <h2 className="font-semibold text-base font-mono truncate">
                {projectName || 'Proyecto Sin Nombre'}
              </h2>
              <p className="text-xs text-muted-foreground caption">
                ID: {projectId || 'N/A'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center gap-2">
              {actions?.map((action) => (
                <Button
                  key={action?.id}
                  variant={action?.variant}
                  size="default"
                  onClick={action?.onClick}
                  iconName={action?.icon}
                  iconPosition="left"
                  title={action?.tooltip}
                  className="whitespace-nowrap"
                >
                  <span className="hidden lg:inline">{action?.label}</span>
                </Button>
              ))}
            </div>

            <div className="md:hidden flex items-center gap-2">
              {actions?.slice(0, 2)?.map((action) => (
                <Button
                  key={action?.id}
                  variant="outline"
                  size="icon"
                  onClick={action?.onClick}
                  iconName={action?.icon}
                  title={action?.label}
                />
              ))}
            </div>

            <div className="relative" ref={moreMenuRef}>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowMoreMenu(!showMoreMenu)}
                iconName="MoreVertical"
                title="Más acciones"
              />

              {showMoreMenu && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-popover border border-border rounded-md shadow-lg overflow-hidden z-50">
                  <div className="py-1">
                    <div className="md:hidden">
                      {actions?.map((action) => (
                        <button
                          key={action?.id}
                          onClick={() => {
                            action?.onClick?.();
                            setShowMoreMenu(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2 text-sm text-foreground hover:bg-muted transition-all duration-250"
                        >
                          <Icon name={action?.icon} size={16} />
                          <span>{action?.label}</span>
                        </button>
                      ))}
                      <div className="my-1 border-t border-border" />
                    </div>
                    
                    {moreActions?.map((action) => (
                      <button
                        key={action?.id}
                        onClick={() => {
                          action?.onClick?.();
                          setShowMoreMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-foreground hover:bg-muted transition-all duration-250"
                      >
                        <Icon name={action?.icon} size={16} />
                        <span>{action?.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectActionToolbar;